<template>
    <div
        v-if="localChat && !loadingChat"
        :key="localChat.id.value"
        class="chat-interaction-list"
    >
        <div ref="chatInteractionListItems" class="chat-interaction-list__items">
            <chat-assistent-interaction-item
                v-if="placeholder"
                :interaction="placeholder"
                :assistent="assistent"
            />
            <component
                :is="getComponentForInteraction(interaction as Interaction)"
                v-for="(interaction, index) in localInteractions"
                :key="interaction.id"
                :assistent="assistent"
                :assistent-goals="assistentGoals"
                :interaction="interaction"
                :chat="chat"
                :url="urls[index]"
            />
        </div>

        <slot name="footer" />
    </div>
    <div v-if="loadingChat" class="chat-interaction-list chat-interaction-list--loading">
        <spinner-loader />
    </div>
</template>

<script setup lang="ts">
    import {
        Assistent,
        Chat,
        ChatConnectGoal,
        GenerateChatTokenCommand,
        Interaction,
        InteractionId,
        InteractionOrigin,
        InteractionType,
    } from '@keyvue/one-api-client';
    import ChatFunctionInteractionItem from '@/ai/assistent/components/interaction/ChatFunctionInteractionItem.vue';
    import ChatUserInteractionItem from '@/ai/assistent/components/interaction/ChatUserInteractionItem.vue';
    import ChatAssistentInteractionItem from '@/ai/assistent/components/interaction/ChatAssistentInteractionItem.vue';
    import { computed, onMounted, ref, watch, nextTick, onUnmounted } from 'vue';
    import ChatClosedInteractionItem from '@/ai/assistent/components/interaction/ChatClosedInteractionItem.vue';
    import ChatEmployeeInteractionItem from '@/ai/assistent/components/interaction/ChatEmployeeInteractionItem.vue';
    import ChatHandoverStartedItem from '@/ai/assistent/components/interaction/ChatHandoverStartedItem.vue';
    import ChatHandoverReferredItem from '@/ai/assistent/components/interaction/ChatHandoverReferredItem.vue';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import MercureConnection from '@/services/keyvue-api/mercure/MercureConnection';
    import useEventBus from '@/services/eventbus/composables/useEventBus';
    import MercureMessageEvent from '@/services/keyvue-api/mercure/MercureMessageEvent';

    const props = defineProps<{
        assistent: Assistent,
        assistentGoals: ChatConnectGoal[],
        chat: Chat;
    }>();

    const localInteractions = ref<Interaction[]>(props.chat.interactions);

    const placeholder = ref();
    const localChat = ref();
    const loadingChat = ref(false);
    const chatInteractionListItems = ref<HTMLElement | null>(null);

    const { client } = useClient();
    const eventBus = useEventBus();

    onMounted(async () => {
        await loadChat();

        placeholder.value = new Interaction(
            localChat.value,
            new InteractionId('test'),
            InteractionType.MESSAGE,
            InteractionOrigin.ASSISTENT,
            null,
            '',
            '',
            props.assistent?.widget?.greeting ?? 'Hallo',
            [],
            null,
            null,
            null,
            null,
            localChat.value.createdAt,
        );

        await startConnection();
        scrollToBottom();
    });

    onUnmounted(async () => {
        if (localChat.value) {
            await abortConnection();
        }
    });

    async function startConnection() {
        const connection = MercureConnection.getInstance(currentConnectionName.value);
        connection.setTokenCallback(() => (
            client.dispatch(GenerateChatTokenCommand.fromChat(localChat.value))
        ));

        await connection.ensureConnection();

        eventBus.subscribe(
            MercureMessageEvent(currentConnectionName.value, 'interaction.completed'),
            onInteractionCompleted,
        );
    }

    async function abortConnection() {
        const connection = MercureConnection.getInstance(currentConnectionName.value);
        connection.abort();
        eventBus.clearComponentSubscribers();
    }

    function onInteractionCompleted(interaction: object) {
        localInteractions.value.push(Interaction.fromObject(interaction));
        scrollToBottom();
    }

    function scrollToBottom() {
        nextTick(() => {
            chatInteractionListItems.value?.scrollTo({
                top: chatInteractionListItems.value?.scrollHeight,
                behavior: 'smooth',
            });
        });
    }

    const urls = computed(() => {
        let url = '';
        return [...localChat.value.interactions].map((interaction) => {
            if (!interaction.url || url === interaction.url) {
                return '';
            }

            url = interaction.url;

            return url;
        });
    });

    function getComponentForInteraction(interaction: Interaction) {
        if (InteractionType.FUNCTION.equals(interaction.type)) {
            return ChatFunctionInteractionItem;
        }

        if (InteractionType.CHAT_CLOSED.equals(interaction.type)) {
            return ChatClosedInteractionItem;
        }

        if (InteractionType.HANDOVER_ACCEPTED.equals(interaction.type)) {
            return ChatHandoverStartedItem;
        }

        if (InteractionType.HANDOVER_REFERRED.equals(interaction.type)) {
            return ChatHandoverReferredItem;
        }

        if (InteractionOrigin.USER.equals(interaction.origin)) {
            return ChatUserInteractionItem;
        }

        if (InteractionOrigin.ASSISTENT.equals(interaction.origin)) {
            return ChatAssistentInteractionItem;
        }

        if (InteractionOrigin.EMPLOYEE.equals(interaction.origin)) {
            return ChatEmployeeInteractionItem;
        }

        return null;
    }

    async function loadChat() {
        loadingChat.value = true;
        localChat.value = await client.get(Chat, props.chat.id);
        localInteractions.value = localChat.value.interactions;
        loadingChat.value = false;
    }

    const currentConnectionName = computed(() => (`chat--${localChat.value.id}`));

    watch(() => props.chat, async () => {
        if (props.chat.equals(localChat.value)) {
            return;
        }

        await abortConnection();
        await loadChat();
        await startConnection();
        scrollToBottom();
    });
</script>

<style scoped lang="scss">
    .chat-interaction-list {
        display: grid;
        grid-template-rows: 1fr min-content;
        height: 100%;
        overflow-y: scroll;
        box-sizing: border-box;

        &__items {
            display: flex;
            flex-direction: column;
            overflow-y: scroll;
            padding: size(md);
            gap: size(md);
            box-sizing: border-box;
        }

        &--loading {
            flex-direction: row;
            justify-content: center;
            align-items: center;
        }

        &__handover-popup {
            width: 100%;
            height: 100px;
            background-color: white;
        }
    }
</style>
